import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onValue, ref, runTransaction } from 'firebase/database';

import { database } from '../services/firebase';

export type SpeciesCatalogOption = string;

type CatalogContextValue = {
  speciesOptions: SpeciesCatalogOption[];
  breedOptionsBySpecies: Record<string, string[]>;
  loadingSpecies: boolean;
  speciesError: Error | null;
};

type SpeciesSeedConfig = {
  value: SpeciesCatalogOption;
  order: number;
  breeds: string[];
};

type ParsedSpeciesEntry = {
  value: SpeciesCatalogOption;
  order: number;
  breeds: string[];
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

const DOG_BREEDS = [
  'Mixed Breed',
  'Australian Cattle Dog',
  'Australian Shepherd',
  'Basset Hound',
  'Beagle',
  'Bernese Mountain Dog',
  'Border Collie',
  'Boston Terrier',
  'Boxer',
  'Brittany Spaniel',
  'Bulldog',
  'Cavalier King Charles Spaniel',
  'Chihuahua',
  'Cocker Spaniel',
  'Collie',
  'Dachshund',
  'Doberman Pinscher',
  'English Springer Spaniel',
  'French Bulldog',
  'German Shepherd',
  'Golden Retriever',
  'Great Dane',
  'Havanese',
  'Labrador Retriever',
  'Maltese',
  'Miniature Schnauzer',
  'Pembroke Welsh Corgi',
  'Pomeranian',
  'Poodle',
  'Rottweiler',
  'Sheltie (Shetland Sheepdog)',
  'Shiba Inu',
  'Siberian Husky',
  'Vizsla',
  'Weimaraner',
  'Yorkshire Terrier',
  'Unknown'
];

const CAT_BREEDS = [
  'Abyssinian',
  'American Shorthair',
  'Bengal',
  'Birman',
  'British Shorthair',
  'Burmese',
  'Devon Rex',
  'Domestic Longhair',
  'Domestic Shorthair',
  'Maine Coon',
  'Norwegian Forest Cat',
  'Oriental Shorthair',
  'Persian',
  'Ragdoll',
  'Russian Blue',
  'Scottish Fold',
  'Siamese',
  'Siberian',
  'Sphynx',
  'Tonkinese',
];

const DEFAULT_SPECIES_DATA: SpeciesSeedConfig[] = [
  { value: 'Dog', order: 0, breeds: DOG_BREEDS },
  { value: 'Cat', order: 1, breeds: CAT_BREEDS },
];

const DEFAULT_SPECIES: SpeciesCatalogOption[] = DEFAULT_SPECIES_DATA.map(entry => entry.value);

const DEFAULT_BREEDS_BY_SPECIES: Record<string, string[]> = DEFAULT_SPECIES_DATA.reduce<Record<string, string[]>>(
  (acc, entry) => {
    acc[entry.value] = [...entry.breeds];
    return acc;
  },
  {}
);

const CatalogContext = createContext<CatalogContextValue | undefined>(undefined);

function toCatalogKey(input: string): string {
  return input.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function mergeBreeds(base: string[], next: string[]): string[] {
  if (!next.length) {
    return base;
  }
  const seen = new Set(base);
  const merged = [...base];
  for (const item of next) {
    if (!seen.has(item)) {
      merged.push(item);
      seen.add(item);
    }
  }
  return merged;
}

function parseBreeds(raw: unknown): string[] {
  if (!raw) {
    return [];
  }

  const entries: { value: string; order: number }[] = [];

  if (Array.isArray(raw)) {
    raw.forEach((item, index) => {
      if (typeof item === 'string') {
        const trimmed = item.trim();
        if (trimmed) {
          entries.push({ value: trimmed, order: index });
        }
        return;
      }
      if (isPlainObject(item)) {
        const candidate =
          typeof item.value === 'string'
            ? (item.value as string)
            : typeof item.label === 'string'
            ? (item.label as string)
            : null;
        if (candidate) {
          const trimmed = candidate.trim();
          if (trimmed) {
            const rawOrder = item.order;
            const order =
              typeof rawOrder === 'number' && Number.isFinite(rawOrder) ? (rawOrder as number) : index;
            entries.push({ value: trimmed, order });
          }
        }
      }
    });
  } else if (typeof raw === 'object') {
    Object.values(raw as Record<string, unknown>).forEach(item => {
      if (typeof item === 'string') {
        const trimmed = item.trim();
        if (trimmed) {
          entries.push({ value: trimmed, order: Number.POSITIVE_INFINITY });
        }
        return;
      }
      if (isPlainObject(item)) {
        const candidate =
          typeof item.value === 'string'
            ? (item.value as string)
            : typeof item.label === 'string'
            ? (item.label as string)
            : null;
        if (candidate) {
          const trimmed = candidate.trim();
          if (trimmed) {
            const rawOrder = item.order;
            const order =
              typeof rawOrder === 'number' && Number.isFinite(rawOrder)
                ? (rawOrder as number)
                : Number.POSITIVE_INFINITY;
            entries.push({ value: trimmed, order });
          }
        }
      }
    });
  } else if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed) {
      entries.push({ value: trimmed, order: 0 });
    }
  }

  if (!entries.length) {
    return [];
  }

  entries.sort((a, b) => a.order - b.order);
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const entry of entries) {
    if (!seen.has(entry.value)) {
      seen.add(entry.value);
      unique.push(entry.value);
    }
  }
  return unique;
}

function parseSpeciesEntry(raw: unknown, fallbackOrder: number): ParsedSpeciesEntry | null {
  if (!raw) {
    return null;
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }
    return {
      value: trimmed,
      order: fallbackOrder,
      breeds: [],
    };
  }

  if (isPlainObject(raw)) {
    const candidate =
      typeof raw.value === 'string'
        ? (raw.value as string)
        : typeof raw.label === 'string'
        ? (raw.label as string)
        : null;
    if (!candidate) {
      return null;
    }
    const trimmed = candidate.trim();
    if (!trimmed) {
      return null;
    }
    const rawOrder = raw.order;
    const order = typeof rawOrder === 'number' && Number.isFinite(rawOrder) ? (rawOrder as number) : fallbackOrder;
    const breeds = parseBreeds(raw.breeds);
    return {
      value: trimmed,
      order,
      breeds,
    };
  }

  return null;
}

function parseSpeciesCatalog(data: unknown): {
  speciesOptions: SpeciesCatalogOption[];
  breedOptionsBySpecies: Record<string, string[]>;
} {
  if (!data) {
    return {
      speciesOptions: [...DEFAULT_SPECIES],
      breedOptionsBySpecies: { ...DEFAULT_BREEDS_BY_SPECIES },
    };
  }

  const entries: ParsedSpeciesEntry[] = [];

  if (Array.isArray(data)) {
    data.forEach((item, index) => {
      const parsed = parseSpeciesEntry(item, index);
      if (parsed) {
        entries.push(parsed);
      }
    });
  } else if (typeof data === 'object') {
    Object.values(data as Record<string, unknown>).forEach(item => {
      const parsed = parseSpeciesEntry(item, Number.POSITIVE_INFINITY);
      if (parsed) {
        entries.push(parsed);
      }
    });
  } else if (typeof data === 'string') {
    const parsed = parseSpeciesEntry(data, 0);
    if (parsed) {
      entries.push(parsed);
    }
  }

  if (!entries.length) {
    return {
      speciesOptions: [...DEFAULT_SPECIES],
      breedOptionsBySpecies: { ...DEFAULT_BREEDS_BY_SPECIES },
    };
  }

  const aggregated = new Map<string, { order: number; breeds: string[] }>();
  for (const entry of entries) {
    const existing = aggregated.get(entry.value);
    if (existing) {
      existing.order = Math.min(existing.order, entry.order);
      existing.breeds = mergeBreeds(existing.breeds, entry.breeds);
    } else {
      aggregated.set(entry.value, {
        order: entry.order,
        breeds: [...entry.breeds],
      });
    }
  }

  const sorted = Array.from(aggregated.entries()).sort((a, b) => a[1].order - b[1].order);
  if (!sorted.length) {
    return {
      speciesOptions: [...DEFAULT_SPECIES],
      breedOptionsBySpecies: { ...DEFAULT_BREEDS_BY_SPECIES },
    };
  }

  const speciesOptions: SpeciesCatalogOption[] = [];
  const breedOptionsBySpecies: Record<string, string[]> = {};
  for (const [value, meta] of sorted) {
    if (!speciesOptions.includes(value)) {
      speciesOptions.push(value);
      breedOptionsBySpecies[value] = [...meta.breeds];
    }
  }

  return {
    speciesOptions: speciesOptions.length ? speciesOptions : [...DEFAULT_SPECIES],
    breedOptionsBySpecies:
      Object.keys(breedOptionsBySpecies).length > 0 ? breedOptionsBySpecies : { ...DEFAULT_BREEDS_BY_SPECIES },
  };
}

function seedSpeciesCatalogOnce(): Promise<void> {
  const speciesRef = ref(database, 'catalogs/species');
  return runTransaction(speciesRef, currentValue => {
    if (currentValue !== undefined && currentValue !== null) {
      return currentValue;
    }
    const seeded: Record<
      string,
      {
        label: SpeciesCatalogOption;
        value: SpeciesCatalogOption;
        order: number;
        breeds?: Record<string, { label: string; value: string; order: number }>;
      }
    > = {};
    DEFAULT_SPECIES_DATA.forEach(entry => {
      const key = toCatalogKey(entry.value);
      let breedsPayload: Record<string, { label: string; value: string; order: number }> | undefined;
      if (entry.breeds.length) {
        breedsPayload = {};
        entry.breeds.forEach((breed, index) => {
          const breedKey = toCatalogKey(`${entry.value}-${breed}`);
          breedsPayload![breedKey] = { label: breed, value: breed, order: index };
        });
      }
      seeded[key] = {
        label: entry.value,
        value: entry.value,
        order: entry.order,
        ...(breedsPayload ? { breeds: breedsPayload } : {}),
      };
    });
    return seeded;
  })
    .then(() => undefined)
    .catch(error => {
      console.warn('Failed to seed species catalog', error);
    });
}

export const CatalogProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [speciesOptions, setSpeciesOptions] = useState<SpeciesCatalogOption[]>([...DEFAULT_SPECIES]);
  const [breedOptionsBySpecies, setBreedOptionsBySpecies] = useState<Record<string, string[]>>({
    ...DEFAULT_BREEDS_BY_SPECIES,
  });
  const [loadingSpecies, setLoadingSpecies] = useState(true);
  const [speciesError, setSpeciesError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;
    const speciesRef = ref(database, 'catalogs/species');

    void seedSpeciesCatalogOnce();

    const unsubscribe = onValue(
      speciesRef,
      snapshot => {
        if (!isMounted) {
          return;
        }
        const parsed = parseSpeciesCatalog(snapshot.val());
        setSpeciesOptions(parsed.speciesOptions);
        setBreedOptionsBySpecies(parsed.breedOptionsBySpecies);
        setLoadingSpecies(false);
        setSpeciesError(null);
      },
      error => {
        if (!isMounted) {
          return;
        }
        console.error('Failed to load species catalog', error);
        setSpeciesOptions([...DEFAULT_SPECIES]);
        setBreedOptionsBySpecies({ ...DEFAULT_BREEDS_BY_SPECIES });
        setLoadingSpecies(false);
        setSpeciesError(error instanceof Error ? error : new Error(String(error)));
      }
    );

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const value = useMemo<CatalogContextValue>(
    () => ({
      speciesOptions,
      breedOptionsBySpecies,
      loadingSpecies,
      speciesError,
    }),
    [speciesOptions, breedOptionsBySpecies, loadingSpecies, speciesError]
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
};

export function useCatalogs(): CatalogContextValue {
  const ctx = useContext(CatalogContext);
  if (!ctx) {
    throw new Error('useCatalogs must be used within a CatalogProvider');
  }
  return ctx;
}
